import { Autocomplete,Skeleton,Stack,TextField,Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import { userReportsType } from '../../types/statistics/types';
import PowerBiNode from '../../service/PowerBiNode';
import ModalInvalid from '../Statistics/ModalInvalid';
import { FullHeightStatisticsContainer } from './StatisticsStyle';

const servicePowerBi = new PowerBiNode();

export default function Dashboard() {
  const { openModal, userData } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [selectedReport, setSelectedReport] = useState<userReportsType | null>(null);
  const [userReports, setUserReports] = useState<userReportsType[]>([]);

  const { data, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: ['GET_EMPRESA_REPORTS'],
    queryFn: async () => {
      try {
        const promise = toast.promise(async () => {
          toast.info(t('Dashboard.loadingWorkspace'));
          const token = await AttToken();
          if (!token) throw new Error(t('Dashboard.tokenInvalid'));
          if (token) {
            const response = await servicePowerBi.getEmpresaReports(token, token.userEmpresa);
            if (!response) throw new Error(t('Dashboard.workspaceNotFound'));
            if (response.error === "Permition Denied - User not allowed to call this API") {
              toast.error(t('Dashboard.permissionDenied'));
              return;
            }
            if (!response) throw new Error(t('Dashboard.workspaceNotFound'));
            return response;
          }
        }, {
          pending: t('Dashboard.loadingWorkspace'),
          error: t('Dashboard.workspaceLoadError')
        }, { toastId: 'get-empresa-reports' });
        return promise;
      } catch (err) {
        throw new Error(t('Dashboard.reportsLoadError'));
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0
  });

  const { data: embedToken, isLoading: isLoadingEmbed, isFetching: isFetchingEmbed, isError: embedError } = useQuery({
    queryKey: ['GET_EMBED_TOKEN', selectedReport],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('Dashboard.tokenInvalid'));
        if (token && selectedReport) {
          const response = await servicePowerBi.getEmbedToken(
            token,
            token.username,
            selectedReport.datasetWorkspaceId as string,
            selectedReport.id as string,
            token.userEmpresa
          );
          if (!response) throw new Error(t('Dashboard.embedTokenLoadError'));
          return response;
        }
      } catch (err) {
        throw new Error(t('Dashboard.embedTokenLoadError'));
      }
    },
    enabled: !selectedReport ? false : true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0
  });

  function convertApiResponseToArray(apiResponse: Record<string, Record<string, userReportsType>>) {
    let allDatasets: userReportsType[] = [];
    for (const workspaceId in apiResponse) {
      if (Object.prototype.hasOwnProperty.call(apiResponse, workspaceId)) {
        const workspace: Record<string, userReportsType> = apiResponse[workspaceId];
        const datasets: userReportsType[] = Object.values(workspace);
        allDatasets = allDatasets.concat(datasets);
      }
    }
    return setUserReports(allDatasets);
  }

  useEffect(() => {
    if (isSuccess && data.reports === null) {
      toast.info(t('Dashboard.noWorkspaceRegistered'));
    }
    if (isSuccess && data.reports) {
      toast.success(t('Dashboard.workspaceFound'));
      convertApiResponseToArray(data.reports);
    }
  }, [data, isSuccess, t]);

  const onLoading = isLoading || isFetching || isLoadingEmbed || isFetchingEmbed;

  useEffect(() => {
    if (embedError) trackEventMatomo('dashboard', 'erro', 'carregamento', 'falha ao carregar dashboard personalizado');
  }, [embedError]);

  return (
    <Stack direction='column' alignItems='center' justifyContent='center' spacing={2} width='100%'>
      <Stack
        width='90%'
        alignItems='center'
        justifyContent='center'
      >
        {onLoading ?
          <Skeleton variant='rounded' height={50} width='90%' />
          : <>
            {openModal && <ModalInvalid openModal={openModal} />}
            <Autocomplete
              sx={{ width: '90%' }}
              onClick={() => trackEventMatomo('dashboard', 'click', 'select', 'seleciona dashboard')}
              id="combo-box-demo"
              filterSelectedOptions
              noOptionsText={t('Dashboard.noConfiguredReports')}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              groupBy={(option) => option?.reportType === 'reportBuilder' ? t('Dashboard.reportBuilder') : t('Dashboard.powerBi')}
              disabled={!userReports?.length}
              options={userReports
                ?.filter((report: userReportsType) => Object.keys(report?.users)?.includes(userData?.id))
                ?.sort((a: userReportsType, b: userReportsType) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()))
                ?.map((report: userReportsType) => ({
                  ...report
                })) ?? []}
              onChange={(_event, newValue: userReportsType | null) => {
                if (newValue) trackEventMatomo('dashboard', 'click', 'select', 'carrega dashboard personalizado');
                setSelectedReport(newValue);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span>{option.name}</span>
                </li>)}
              renderInput={(params) => <TextField {...params} label={t('Dashboard.Relatorios_configurados')} />}
            />
          </>
        }
      </Stack>
      {
        selectedReport?.name?.length && onLoading ?
          <Skeleton variant='rounded' height='100vh' width='100%' />
          : embedError ? <Typography variant='h5' color='textDisabled' textAlign='center'>{t('Dashboard.accessError')}</Typography> :
           embedToken && <FullHeightStatisticsContainer>
              <PowerBIEmbed
                key={selectedReport?.reportId}
                cssClassName="powerBiStyle"
                embedConfig={{
                  type: "report", // Supported types: report, dashboard, tile, visual and qna
                  id: selectedReport?.id,
                  embedUrl: embedToken?.embedUrl?.embedUrl,
                  accessToken: embedToken?.embedToken, // Keep as empty string, null or undefined
                  tokenType: models.TokenType.Embed, // PowerBiClient.Models.TokenType.Embed
                  settings: {
                    panes: {
                      filters: {
                        expanded: true,
                        visible: true
                      }
                    },
                  }
                }}
                eventHandlers={new Map([
                  ['loaded', () => trackEventMatomo('dashboard', 'sucesso', 'carregamento', 'dashboard personalizado carregado')],
                  ['error', () => trackEventMatomo('dashboard', 'erro', 'carregamento', 'erro no dashboard personalizado')],
                ])}
              />
            </FullHeightStatisticsContainer>}
    </Stack>
  );
}
